import * as THREE from "three";

// Minimal data the VR UI needs from the reveal (structurally satisfied by RevealPayload).
export interface VrReveal {
  score: {
    roundScore: number;
    baseRoundScore: number;
    distanceKm: number | null;
    yearsOff: number;
    locationScore: number;
    timeScore: number;
    speedBonus: number;
    timeDecayYears: number;
    sameCountry: boolean;
    sameContinent: boolean;
    countryBonus: number;
    continentBonus: number;
    wager: number;
    comebackMultiplier: number;
  };
  answer: { title: string; place: string; dateLabel: string; lat: number; lng: number };
  totals: { totalScore: number; roundsCompleted: number; totalDistanceKm: number; totalYearsOff: number };
  finished: boolean;
}

export interface VrGameBridge {
  getYear: () => number;
  setYear: (year: number) => void;
  yearBounds: () => { min: number; max: number; start: number };   // current round's era-calibrated slider range + a start within it
  setPlace: (lat: number, lng: number) => void;
  submit: () => Promise<VrReveal | null>;
  next: () => void;
  isFinal: () => boolean;
  hasSession: () => boolean;        // is a scoreable round loaded right now?
  playAgain: () => Promise<void>;   // start a fresh session (same settings) without leaving VR
}

type Phase = "idle" | "intro" | "year" | "place" | "reveal" | "summary";
type Ring = number[][];
type CountryLabel = { name: string; lng: number; lat: number; rank: number };
type City = { n: string; lat: number; lng: number; r: number; cap: number };

const COUNTRIES_URL = "/vr-countries.json";
const CITIES_URL = "/vr-cities.json";
const INTRO_SECONDS = 10;
const ROUND_SECONDS = 90;
const HUD_DIST = 1.2;       // metres in front of the user (clear of the 0.1m near plane)
const HUD_DROP = 0.12;      // metres below eye line

function formatYear(y: number): string {
  const r = Math.round(y);
  return r < 0 ? `${-r} BCE` : `${r} CE`;
}

function makePanel(widthM: number, heightM: number, pxW: number, order: number, opacity = 1): {
  mesh: THREE.Mesh;
  ctx: CanvasRenderingContext2D;
  texture: THREE.CanvasTexture;
} {
  const canvas = document.createElement("canvas");
  canvas.width = pxW;
  canvas.height = Math.round((pxW * heightM) / widthM);
  const ctx = canvas.getContext("2d")!;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity, side: THREE.DoubleSide, depthTest: false, depthWrite: false });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(widthM, heightM), material);
  mesh.renderOrder = order;            // stable compositing -> no z-fighting flicker
  mesh.frustumCulled = false;          // world-locked HUD: never cull, so a panel can't vanish when you look toward its edge
  return { mesh, ctx, texture };
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export class VrGameUi {
  private phase: Phase = "idle";
  private readonly hud = new THREE.Group();
  private readonly raycaster = new THREE.Raycaster();
  private interactives: THREE.Mesh[] = [];
  private controllers: THREE.Group[] = [];
  private reticle: THREE.Mesh;
  private globe: THREE.Mesh;   // 3D globe you point at + spin to place the guess (replaces the flat map)

  private intro = makePanel(0.5, 0.12, 768, 10, 0.85);
  private yearPanel = makePanel(0.62, 0.24, 1024, 10, 0.85);
  private lockBtn = makePanel(0.34, 0.11, 512, 11);
  private mapPanel = makePanel(1.2, 0.6, 2048, 10, 0.85);
  private submitBtn = makePanel(0.34, 0.11, 512, 11);
  private revealPanel = makePanel(0.92, 0.46, 1280, 10, 0.85);
  private nextBtn = makePanel(0.34, 0.11, 512, 11);
  private againBtn = makePanel(0.4, 0.12, 600, 11);
  private exitBtn = makePanel(0.3, 0.1, 512, 11);

  private countries: Ring[][] = [];   // each country = array of rings
  private countryLabels: CountryLabel[] = [];
  private cities: City[] = [];
  private zoom = 1;
  private centerLng = 0;
  private centerLat = 20;
  private hoverLatLng: { lat: number; lng: number } | null = null;
  private pendingGuess: { lat: number; lng: number } | null = null;
  private lastReveal: VrReveal | null = null;

  private introLeft = 0;
  private timeLeft = 0;
  private lastT = 0;
  private busy = false;
  private prevGrip = false;
  private yearLo = 0;
  private yearHi = 1;
  private yearVal = 0;   // precise (unrounded) slider year, so narrow eras accumulate sub-year nudges

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly scene: THREE.Scene,
    private readonly bridge: VrGameBridge
  ) {
    this.scene.add(this.hud);
    this.hud.visible = false;
    this.hud.scale.setScalar(0.85);   // a touch smaller / less in-your-face

    // lay everything out in HUD-local space (the HUD is placed in front of the
    // user once per phase; it does NOT follow the head every frame).
    this.intro.mesh.position.set(0, 0.0, 0);
    this.yearPanel.mesh.position.set(0, 0.06, 0);
    this.lockBtn.mesh.position.set(0, -0.13, 0);
    this.tagAction(this.lockBtn.mesh, "lockYear");
    this.mapPanel.mesh.position.set(0, 0.04, 0);
    this.tagAction(this.mapPanel.mesh, "mapPick");
    this.submitBtn.mesh.position.set(0, -0.34, 0);
    this.tagAction(this.submitBtn.mesh, "submit");
    this.revealPanel.mesh.position.set(0, 0.05, 0);
    this.nextBtn.mesh.position.set(0, -0.32, 0);
    this.tagAction(this.nextBtn.mesh, "next");
    this.tagAction(this.againBtn.mesh, "again");
    this.tagAction(this.exitBtn.mesh, "exit");

    for (const p of [this.intro, this.yearPanel, this.lockBtn, this.mapPanel, this.submitBtn, this.revealPanel, this.nextBtn, this.againBtn, this.exitBtn]) {
      this.hud.add(p.mesh);
      p.mesh.visible = false;
    }

    // The globe wears the SAME 2:1 equirect canvas the flat map already draws (countries,
    // labels, pins, arc) — so wrapping it on a sphere reuses all of drawMap(). You point at
    // it to drop a pin and spin it to reach the far side; the flat mapPanel plane is retired.
    this.globe = new THREE.Mesh(
      new THREE.SphereGeometry(0.34, 64, 48),
      new THREE.MeshBasicMaterial({ map: this.mapPanel.texture, depthTest: false, depthWrite: false })
    );
    this.globe.renderOrder = 8;          // behind the HUD panels/buttons (renderOrder 10-11)
    this.globe.frustumCulled = false;
    this.globe.visible = false;
    this.tagAction(this.globe, "globePick");
    this.hud.add(this.globe);

    for (let i = 0; i < 2; i++) {
      const c = this.renderer.xr.getController(i);
      const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -2)]);
      const mat = new THREE.LineBasicMaterial({ color: 0x9fefff, transparent: true, opacity: 0.55, depthTest: false });
      const line = new THREE.Line(geo, mat);
      line.renderOrder = 20;
      c.add(line);
      c.addEventListener("selectstart", () => this.onSelect(c));
      this.scene.add(c);
      this.controllers.push(c);
    }
    this.reticle = new THREE.Mesh(
      new THREE.SphereGeometry(0.012, 16, 12),
      new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false })
    );
    this.reticle.renderOrder = 21;
    this.reticle.visible = false;
    this.scene.add(this.reticle);

    this.renderer.xr.addEventListener("sessionstart", () => void this.startRound());
    this.renderer.xr.addEventListener("sessionend", () => {
      this.phase = "idle";
      this.hud.visible = false;
      this.reticle.visible = false;
    });

    fetch(COUNTRIES_URL)
      .then((r) => r.json())
      .then((geo: { features: Array<{ geometry: { type: string; coordinates: unknown }; properties?: Record<string, unknown> }> }) => {
        for (const f of geo.features) {
          const g = f.geometry;
          if (g) {
            if (g.type === "Polygon") this.countries.push(g.coordinates as Ring[]);
            else if (g.type === "MultiPolygon") for (const poly of g.coordinates as Ring[][]) this.countries.push(poly);
          }
          const p = f.properties;
          if (p && typeof p.LABEL_X === "number" && typeof p.LABEL_Y === "number") {
            this.countryLabels.push({ name: String(p.NAME ?? p.ADMIN ?? ""), lng: p.LABEL_X, lat: p.LABEL_Y, rank: Number(p.LABELRANK ?? 6) });
          }
        }
        if (this.phase === "place" || this.phase === "reveal") this.drawMap();
      })
      .catch(() => { /* map falls back to graticule only */ });

    fetch(CITIES_URL)
      .then((r) => r.json())
      .then((list: City[]) => {
        this.cities = list;
        if (this.phase === "place" || this.phase === "reveal") this.drawMap();
      })
      .catch(() => { /* city labels are optional */ });
  }

  private tagAction(mesh: THREE.Mesh, action: string): void {
    mesh.userData.action = action;
  }

  // ---- world-locked placement (called on each phase change / grip recenter) ----
  private recenter(): void {
    const cam = this.renderer.xr.getCamera();
    const p = new THREE.Vector3();
    const q = new THREE.Quaternion();
    cam.getWorldPosition(p);
    cam.getWorldQuaternion(q);
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(q);
    fwd.y = 0;
    if (fwd.lengthSq() < 1e-4) fwd.set(0, 0, -1);
    fwd.normalize();
    this.hud.position.copy(p).addScaledVector(fwd, HUD_DIST);
    this.hud.position.y = p.y - HUD_DROP;
    this.hud.rotation.set(0, Math.atan2(-fwd.x, -fwd.z), 0); // upright, +Z faces the user
  }

  // ---- phases ------------------------------------------------------------
  private async startRound(): Promise<void> {
    // Entered VR straight from the landing page (no game running)? Start one, so
    // there's a real round to calibrate the year slider to and to score against.
    if (!this.bridge.hasSession()) {
      this.hud.visible = true;
      this.recenter();
      this.show([this.intro.mesh]);
      this.drawLoading("Starting game…");
      try {
        await this.bridge.playAgain();
      } catch {
        /* offline — fall through; the submit step will surface a retry */
      }
    }
    this.pendingGuess = null;
    this.lastReveal = null;
    this.zoom = 1;
    this.centerLng = 0;
    this.centerLat = 20;
    const yb = this.bridge.yearBounds();
    this.yearLo = yb.min;
    this.yearHi = yb.max;
    this.yearVal = yb.start;
    this.bridge.setYear(yb.start);
    this.phase = "intro";
    this.introLeft = INTRO_SECONDS;
    this.hud.visible = true;
    this.recenter();
    this.show([this.intro.mesh]);
  }

  private toYear(): void {
    this.phase = "year";
    this.timeLeft = ROUND_SECONDS;
    this.recenter();
    this.show([this.yearPanel.mesh, this.lockBtn.mesh]);
    this.interactives = [this.lockBtn.mesh];
    this.drawYear();
    this.drawButton(this.lockBtn, "Lock Year ▸");
  }

  private toPlace(): void {
    this.phase = "place";
    this.recenter();
    this.zoom = 1; this.centerLng = 0; this.centerLat = 0;   // globe = full world (spin, no pan/zoom)
    this.globe.scale.setScalar(1);
    this.globe.position.set(0, 0, 0);
    this.globe.rotation.set(0, -Math.PI / 2, 0);            // prime meridian facing the user
    this.submitBtn.mesh.position.set(0, -0.52, 0);
    this.show([this.globe, this.submitBtn.mesh]);
    this.interactives = [this.globe, this.submitBtn.mesh];
    this.drawMap();
    this.drawButton(this.submitBtn, this.pendingGuess ? "Submit ▸" : "Point + trigger", this.pendingGuess !== null);
  }

  private async onSubmit(): Promise<void> {
    if (!this.pendingGuess || this.busy) return;
    this.busy = true;
    this.bridge.setPlace(this.pendingGuess.lat, this.pendingGuess.lng);
    this.drawButton(this.submitBtn, "Scoring…", false);
    let reveal: VrReveal | null = null;
    try {
      reveal = await this.bridge.submit();
    } catch {
      reveal = null;
    }
    this.busy = false;
    if (reveal) this.toReveal(reveal);
    else this.drawButton(this.submitBtn, "Retry ▸", true);
  }

  private toReveal(reveal: VrReveal): void {
    this.phase = "reveal";
    this.lastReveal = reveal;
    this.recenter();
    // globe shows the whole world + the guess→answer arc, spun to face the answer location
    this.zoom = 1; this.centerLng = 0; this.centerLat = 0;
    this.globe.scale.setScalar(0.78);
    this.globe.position.set(0, -0.12, 0);
    this.globe.rotation.set(
      THREE.MathUtils.clamp(reveal.answer.lat * Math.PI / 180, -1.2, 1.2),
      -Math.PI / 2 - reveal.answer.lng * Math.PI / 180,
      0
    );
    this.show([this.globe, this.revealPanel.mesh, this.nextBtn.mesh]);
    this.revealPanel.mesh.position.set(0, 0.44, 0);
    this.nextBtn.mesh.position.set(0, -0.56, 0);
    this.drawMap();
    this.drawReveal(reveal);
    this.drawButton(this.nextBtn, reveal.finished ? "See Results ▸" : "Next ▸");
    this.interactives = [this.nextBtn.mesh];
  }

  private onNext(): void {
    // restore place-phase layout for the next round
    this.mapPanel.mesh.position.set(0, 0.04, 0);
    this.submitBtn.mesh.position.set(0, -0.34, 0);
    if (this.lastReveal?.finished) {
      this.toSummary(this.lastReveal);   // stay in VR for the final score + play-again
      return;
    }
    this.bridge.next();
    void this.startRound();
  }

  // ---- end-of-game summary (stays in VR; offers play-again) --------------
  private toSummary(reveal: VrReveal): void {
    this.phase = "summary";
    this.recenter();
    this.revealPanel.mesh.position.set(0, 0.08, 0);
    this.againBtn.mesh.position.set(0, -0.28, 0);
    this.exitBtn.mesh.position.set(0, -0.45, 0);
    this.show([this.revealPanel.mesh, this.againBtn.mesh, this.exitBtn.mesh]);
    this.drawSummary(reveal);
    this.drawButton(this.againBtn, "Play Again ▸");
    this.drawButton(this.exitBtn, "Exit VR");
    this.interactives = [this.againBtn.mesh, this.exitBtn.mesh];
  }

  private async onPlayAgain(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.drawButton(this.againBtn, "Loading…", false);
    try {
      await this.bridge.playAgain();
    } catch {
      /* if the new session fails to load, fall through and let startRound retry */
    }
    this.busy = false;
    // restore the per-round panel layout the reveal/summary phases rearranged
    this.mapPanel.mesh.position.set(0, 0.04, 0);
    this.revealPanel.mesh.position.set(0, 0.05, 0);
    void this.startRound();
  }

  private forceSubmit(): void {
    if (this.phase === "year") this.toPlace();
    if (!this.pendingGuess) {
      this.pendingGuess = this.hoverLatLng ?? { lat: this.centerLat, lng: this.centerLng };
    }
    void this.onSubmit();
  }

  // ---- input -------------------------------------------------------------
  private onSelect(controller: THREE.Group): void {
    const hit = this.raycast(controller);
    if (!hit) return;
    const action = hit.object.userData.action as string | undefined;
    if (action === "lockYear" && this.phase === "year") {
      this.toPlace();
    } else if (action === "globePick" && this.phase === "place") {
      this.pendingGuess = this.hitToLatLng(hit);
      this.drawMap();
      this.drawButton(this.submitBtn, "Submit ▸", true);
    } else if (action === "submit" && this.phase === "place") {
      void this.onSubmit();
    } else if (action === "next" && this.phase === "reveal") {
      this.onNext();
    } else if (action === "again" && this.phase === "summary") {
      void this.onPlayAgain();
    } else if (action === "exit" && this.phase === "summary") {
      void this.renderer.xr.getSession()?.end();
    }
  }

  private raycast(controller: THREE.Group): THREE.Intersection | null {
    const origin = new THREE.Vector3();
    const dir = new THREE.Vector3(0, 0, -1);
    controller.updateMatrixWorld();
    origin.setFromMatrixPosition(controller.matrixWorld);
    dir.applyQuaternion(controller.getWorldQuaternion(new THREE.Quaternion())).normalize();
    this.raycaster.set(origin, dir);
    const hits = this.raycaster.intersectObjects(this.interactives, false);
    return hits.length ? hits[0] : null;
  }

  update(): void {
    if (!this.renderer.xr.isPresenting) {
      if (this.hud.visible) this.hud.visible = false;
      return;
    }
    const now = performance.now();
    const dt = this.lastT ? Math.min(0.05, (now - this.lastT) / 1000) : 0;
    this.lastT = now;

    const sticks = this.readSticks();
    if (sticks.gripEdge) this.recenter();

    if (this.phase === "intro") {
      this.introLeft -= dt;
      this.drawIntro();
      if (this.introLeft <= 0) this.toYear();
    } else if (this.phase === "year") {
      this.timeLeft -= dt;
      this.scrubYear(sticks.anyX, dt);
      this.drawYear();
      if (this.timeLeft <= 0) this.forceSubmit();
    } else if (this.phase === "place") {
      this.timeLeft -= dt;
      this.spinGlobe(sticks, dt);   // spin rotates the mesh; texture is static so no redraw
      if (this.timeLeft <= 0) this.forceSubmit();
    }
    this.updateReticle();
  }

  private readSticks(): { leftY: number; rightX: number; rightY: number; anyX: number; gripEdge: boolean } {
    const session = this.renderer.xr.getSession();
    let leftY = 0, rightX = 0, rightY = 0, anyX = 0, grip = false;
    if (session) {
      for (const src of session.inputSources) {
        const gp = src.gamepad;
        if (!gp) continue;
        const ax = gp.axes.length >= 4 ? [gp.axes[2], gp.axes[3]] : [gp.axes[0] ?? 0, gp.axes[1] ?? 0];
        if (Math.abs(ax[0]) > Math.abs(anyX)) anyX = ax[0];
        if (src.handedness === "left") leftY = ax[1];
        else if (src.handedness === "right") { rightX = ax[0]; rightY = ax[1]; }
        if (gp.buttons[1]?.pressed) grip = true;
      }
    }
    const gripEdge = grip && !this.prevGrip;
    this.prevGrip = grip;
    return { leftY, rightX, rightY, anyX, gripEdge };
  }

  private scrubYear(x: number, dt: number): void {
    if (Math.abs(x) < 0.12) return;
    // Accumulate on a float so sub-year nudges aren't rounded away each frame — that
    // rounding froze the year on narrow eras (e.g. modern's 226-yr span moves <0.5 yr
    // per frame), while ancient's huge span cleared a whole year and still worked.
    const span = this.yearHi - this.yearLo;
    const delta = Math.sign(x) * Math.pow(Math.abs(x), 3) * (span / 8) * dt;
    this.yearVal = Math.max(this.yearLo, Math.min(this.yearHi, this.yearVal + delta));
    this.bridge.setYear(Math.round(this.yearVal));
  }

  private spinGlobe(s: { rightX: number; rightY: number }, dt: number): void {
    // right stick spins the globe: X = yaw, Y = pitch (clamped so it can't flip over the poles)
    if (Math.abs(s.rightX) > 0.15) this.globe.rotation.y += s.rightX * dt * 1.8;
    if (Math.abs(s.rightY) > 0.15) {
      this.globe.rotation.x = THREE.MathUtils.clamp(this.globe.rotation.x + s.rightY * dt * 1.4, -1.3, 1.3);
    }
  }

  private updateReticle(): void {
    let hit: THREE.Intersection | null = null;
    for (const c of this.controllers) {
      const h = this.raycast(c);
      if (h && (!hit || h.distance < hit.distance)) hit = h;
    }
    if (hit) {
      this.reticle.position.copy(hit.point);
      this.reticle.visible = true;
      if (this.phase === "place" && hit.object.userData.action === "globePick") {
        this.hoverLatLng = this.hitToLatLng(hit);
      }
    } else {
      this.reticle.visible = false;
    }
  }

  // ---- map projection ----------------------------------------------------
  private spanLng(): number { return 360 / this.zoom; }
  private spanLat(): number { return 180 / this.zoom; }
  // Controller ray hit on the globe -> lat/lng. Inverse of three's SphereGeometry mapping
  // (texV=1 = canvas top = north pole, with flipY); worldToLocal folds in the globe's spin.
  private hitToLatLng(hit: THREE.Intersection): { lat: number; lng: number } {
    const p = this.globe.worldToLocal(hit.point.clone()).normalize();
    const lat = Math.asin(THREE.MathUtils.clamp(p.y, -1, 1)) * 180 / Math.PI;
    let lng = Math.atan2(p.z, -p.x) * 180 / Math.PI - 180;
    if (lng < -180) lng += 360;
    return { lat, lng };
  }
  private projX(lng: number, w: number): number {
    return ((lng - (this.centerLng - this.spanLng() / 2)) / this.spanLng()) * w;
  }
  private projY(lat: number, h: number): number {
    return (((this.centerLat + this.spanLat() / 2) - lat) / this.spanLat()) * h; // north at canvas top
  }

  private drawMap(): void {
    const { ctx, texture } = this.mapPanel;
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    ctx.fillStyle = "#0b2233";            // ocean
    ctx.fillRect(0, 0, w, h);
    // land + borders
    ctx.lineWidth = 1.2;
    ctx.strokeStyle = "rgba(180,230,255,0.55)";
    ctx.fillStyle = "#244055";
    for (const poly of this.countries) {
      ctx.beginPath();
      for (const ring of poly) {
        for (let i = 0; i < ring.length; i++) {
          const x = this.projX(ring[i][0], w);
          const y = this.projY(ring[i][1], h);
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.closePath();
      }
      ctx.fill();
      ctx.stroke();
    }
    // graticule
    ctx.strokeStyle = "rgba(255,255,255,0.10)";
    ctx.lineWidth = 1;
    for (let lng = -180; lng <= 180; lng += 30) {
      const x = this.projX(lng, w);
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
    }
    for (let lat = -60; lat <= 60; lat += 30) {
      const y = this.projY(lat, h);
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
    }
    this.drawLabels(w, h);
    // pins
    const drawPin = (lat: number, lng: number, color: string) => {
      const x = this.projX(lng, w), y = this.projY(lat, h);
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(x, y, 11, 0, Math.PI * 2); ctx.fill();
      ctx.lineWidth = 3; ctx.strokeStyle = "#06121c"; ctx.stroke();
    };
    if (this.lastReveal && this.pendingGuess) {
      const a = this.lastReveal.answer;
      ctx.strokeStyle = "#ffd166"; ctx.lineWidth = 3; ctx.setLineDash([10, 8]);
      ctx.beginPath();
      ctx.moveTo(this.projX(this.pendingGuess.lng, w), this.projY(this.pendingGuess.lat, h));
      ctx.lineTo(this.projX(a.lng, w), this.projY(a.lat, h));
      ctx.stroke(); ctx.setLineDash([]);
      drawPin(a.lat, a.lng, "#ffd166");
    }
    if (this.pendingGuess) drawPin(this.pendingGuess.lat, this.pendingGuess.lng, "#53e0ff");
    texture.needsUpdate = true;
  }

  // Country + major-city labels, thinned by zoom so the world view stays legible
  // and more detail appears as the player zooms in to pin a spot.
  private drawLabels(w: number, h: number): void {
    const { ctx } = this.mapPanel;
    const inView = (x: number, y: number) => x >= -40 && x <= w + 40 && y >= 0 && y <= h;
    const text = (s: string, x: number, y: number, color: string, px: number) => {
      ctx.font = `600 ${px}px system-ui, sans-serif`;
      ctx.lineWidth = Math.max(2.5, px / 5); ctx.strokeStyle = "rgba(4,12,20,0.85)";
      ctx.strokeText(s, x, y); ctx.fillStyle = color; ctx.fillText(s, x, y);
    };
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    // countries: lower LABELRANK = more prominent; show more as zoom climbs
    const countryMax = 1 + this.zoom * 1.6;
    for (const c of this.countryLabels) {
      if (c.rank > countryMax || !c.name) continue;
      const x = this.projX(c.lng, w), y = this.projY(c.lat, h);
      if (inView(x, y)) text(c.name.toUpperCase(), x, y, "rgba(214,234,246,0.92)", 26);
    }
    // cities: lower scalerank = bigger; capitals (gold) appear a little sooner
    const cityMax = this.zoom * 1.15 - 1;
    for (const c of this.cities) {
      if (c.r - (c.cap ? 1.5 : 0) > cityMax) continue;
      const x = this.projX(c.lng, w), y = this.projY(c.lat, h);
      if (!inView(x, y)) continue;
      ctx.beginPath(); ctx.arc(x, y, c.cap ? 6 : 4.5, 0, Math.PI * 2);
      ctx.fillStyle = c.cap ? "#ffd166" : "rgba(255,255,255,0.85)"; ctx.fill();
      text(c.n, x, y - 16, "rgba(255,238,205,0.95)", 21);
    }
    ctx.textBaseline = "alphabetic";
  }

  // ---- drawing -----------------------------------------------------------
  private show(meshes: THREE.Mesh[]): void {
    const set = new Set(meshes);
    for (const p of [this.intro, this.yearPanel, this.lockBtn, this.mapPanel, this.submitBtn, this.revealPanel, this.nextBtn, this.againBtn, this.exitBtn]) {
      p.mesh.visible = set.has(p.mesh);
    }
    this.globe.visible = set.has(this.globe);
  }

  private clock(): string {
    const s = Math.max(0, Math.ceil(this.timeLeft));
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
  }

  private drawIntro(): void {
    this.drawLoading(`Look around — guess in ${Math.max(0, Math.ceil(this.introLeft))}s`);
  }

  private drawLoading(msg: string): void {
    const { ctx, texture } = this.intro;
    const w = ctx.canvas.width, h = ctx.canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "rgba(8,18,28,0.7)";
    roundRect(ctx, 0, 0, w, h, h / 2); ctx.fill();
    ctx.fillStyle = "#9fefff";
    ctx.font = "600 40px system-ui, sans-serif";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(msg, w / 2, h / 2);
    ctx.textBaseline = "alphabetic";
    texture.needsUpdate = true;
  }

  private drawYear(): void {
    const { ctx, texture } = this.yearPanel;
    const w = ctx.canvas.width, h = ctx.canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "rgba(8,18,28,0.82)";
    roundRect(ctx, 0, 0, w, h, 28); ctx.fill();
    ctx.textAlign = "center";
    ctx.fillStyle = "#9fefff"; ctx.font = "600 30px system-ui, sans-serif";
    ctx.fillText("WHEN?", w / 2 - 120, 46);
    ctx.fillStyle = this.timeLeft < 15 ? "#ff8a8a" : "rgba(255,255,255,0.65)";
    ctx.font = "600 30px system-ui, sans-serif";
    ctx.fillText(`⏱ ${this.clock()}`, w / 2 + 130, 46);
    ctx.fillStyle = "#ffffff"; ctx.font = "700 92px system-ui, sans-serif";
    ctx.fillText(formatYear(this.bridge.getYear()), w / 2, 140);
    const bx = 60, bw = w - 120, by = 180;
    ctx.fillStyle = "rgba(255,255,255,0.18)"; roundRect(ctx, bx, by, bw, 10, 5); ctx.fill();
    const frac = (this.bridge.getYear() - this.yearLo) / (this.yearHi - this.yearLo);
    ctx.fillStyle = "#53e0ff";
    ctx.beginPath(); ctx.arc(bx + bw * Math.max(0, Math.min(1, frac)), by + 5, 14, 0, Math.PI * 2); ctx.fill();
    // era endpoints, so the player can see the scale they're scrubbing within
    ctx.fillStyle = "rgba(255,255,255,0.45)"; ctx.font = "500 20px system-ui, sans-serif";
    ctx.textAlign = "left"; ctx.fillText(formatYear(this.yearLo), bx, by + 42);
    ctx.textAlign = "right"; ctx.fillText(formatYear(this.yearHi), bx + bw, by + 42);
    ctx.textAlign = "center";
    ctx.fillStyle = "rgba(255,255,255,0.5)"; ctx.font = "500 22px system-ui, sans-serif";
    ctx.fillText("push thumbstick ◀ ▶ to scrub the year", w / 2, h - 18);
    texture.needsUpdate = true;
  }

  private drawButton(panel: { ctx: CanvasRenderingContext2D; texture: THREE.CanvasTexture }, label: string, enabled = true): void {
    const { ctx, texture } = panel;
    const w = ctx.canvas.width, h = ctx.canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = enabled ? "rgba(120,230,255,0.95)" : "rgba(90,110,125,0.7)";
    roundRect(ctx, 0, 0, w, h, h / 2); ctx.fill();
    ctx.fillStyle = enabled ? "#04121f" : "#cfe";
    ctx.font = "700 52px system-ui, sans-serif";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(label, w / 2, h / 2 + 2);
    ctx.textBaseline = "alphabetic";
    texture.needsUpdate = true;
  }

  private drawReveal(r: VrReveal): void {
    const { ctx, texture } = this.revealPanel;
    const w = ctx.canvas.width, h = ctx.canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "rgba(8,18,28,0.92)";
    roundRect(ctx, 0, 0, w, h, 28); ctx.fill();
    ctx.textAlign = "center";
    ctx.fillStyle = "#ffd166"; ctx.font = "700 72px system-ui, sans-serif";
    ctx.fillText(`${Math.round(r.score.roundScore).toLocaleString()} pts`, w / 2, 78);
    ctx.fillStyle = "#ffffff"; ctx.font = "600 34px system-ui, sans-serif";
    ctx.fillText(`${r.answer.title} — ${r.answer.place} · ${r.answer.dateLabel}`, w / 2, 124);
    // breakdown
    ctx.textAlign = "left";
    ctx.font = "500 28px system-ui, sans-serif";
    const L = 60; let y = 184;
    const line = (label: string, val: string, c = "#cfe") => {
      ctx.fillStyle = "rgba(255,255,255,0.55)"; ctx.fillText(label, L, y);
      ctx.fillStyle = c; ctx.textAlign = "right"; ctx.fillText(val, w - L, y); ctx.textAlign = "left";
      y += 42;
    };
    const dist = r.score.distanceKm == null ? "—" : `${Math.round(r.score.distanceKm).toLocaleString()} km`;
    line("Place", `${dist} → ${Math.round(r.score.locationScore).toLocaleString()} pts`, "#9fefff");
    if (r.score.countryBonus > 0) line("  right country", `+${r.score.countryBonus}`, "#7f7");
    else if (r.score.continentBonus > 0) line("  right continent", `+${r.score.continentBonus}`, "#7f7");
    line("Time", `${Math.abs(Math.round(r.score.yearsOff))} yrs off → ${Math.round(r.score.timeScore).toLocaleString()} pts`, "#9fefff");
    if (r.score.speedBonus > 0) line("Speed bonus", `+${Math.round(r.score.speedBonus).toLocaleString()}`, "#7f7");
    if (r.score.wager > 1) line("Wager", `×${r.score.wager}`);
    if (r.score.comebackMultiplier > 1) line("Comeback", `×${r.score.comebackMultiplier}`);
    texture.needsUpdate = true;
  }

  private drawSummary(reveal: VrReveal): void {
    const { ctx, texture } = this.revealPanel;
    const w = ctx.canvas.width, h = ctx.canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "rgba(8,18,28,0.94)";
    roundRect(ctx, 0, 0, w, h, 28); ctx.fill();
    const t = reveal.totals;
    ctx.textAlign = "center";
    ctx.fillStyle = "#9fefff"; ctx.font = "600 34px system-ui, sans-serif";
    ctx.fillText("GAME COMPLETE", w / 2, 64);
    ctx.fillStyle = "#ffd166"; ctx.font = "800 96px system-ui, sans-serif";
    ctx.fillText(Math.round(t.totalScore).toLocaleString(), w / 2, 168);
    ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "500 28px system-ui, sans-serif";
    ctx.fillText("total points", w / 2, 206);
    ctx.font = "500 30px system-ui, sans-serif";
    const L = 90; let y = 272;
    const line = (label: string, val: string) => {
      ctx.textAlign = "left"; ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.fillText(label, L, y);
      ctx.textAlign = "right"; ctx.fillStyle = "#cfe"; ctx.fillText(val, w - L, y);
      y += 48;
    };
    line("Rounds played", `${t.roundsCompleted}`);
    line("Total distance off", `${Math.round(t.totalDistanceKm).toLocaleString()} km`);
    line("Total years off", `${Math.round(t.totalYearsOff).toLocaleString()} yrs`);
    texture.needsUpdate = true;
  }
}
